<script lang="ts">
  import type { GrammarError } from '$lib/types/grammar'

  /**
   * ErrorCardController: Handles error card display and filtering
   * Single Responsibility: Manage error list and active error calculations
   */

  interface Props {
    errors: GrammarError[]
    ignoredErrors: Set<string>
    isChecking: boolean
    hasChecked: boolean
    hasText: boolean
    allApplied?: boolean
    processedCount?: number
  }

  let { 
    errors = [],
    ignoredErrors = new Set(),
    isChecking = false,
    hasChecked = false,
    hasText = false,
    allApplied = false,
    processedCount = 0
  }: Props = $props()
  
  // Tips to show users
  const tips = [
    {
      title: '맞춤법 검사를 시작하세요',
      subtitle: 'Ctrl+Enter 또는 아래 버튼을 클릭하여 검사'
    },
    {
      title: 'AI로 문장을 개선해보세요',
      subtitle: '왼쪽 도구모음에서 다양한 AI 기능 사용 가능'
    },
    {
      title: '문장을 이어서 작성해보세요',
      subtitle: 'Shift+Tab으로 AI가 다음 문장 제안'
    },
    {
      title: '선택 영역만 검사할 수 있습니다',
      subtitle: '텍스트를 선택한 후 맞춤법 검사 실행'
    },
    {
      title: '다양한 AI 기능을 활용해보세요',
      subtitle: '바꿔쓰기, 늘려쓰기, 줄여쓰기, 요약하기 등'
    },
    {
      title: '모든 교정을 한 번에 적용하세요',
      subtitle: 'Ctrl+Shift+Enter로 모든 오류 수정'
    },
    {
      title: 'AI가 문장을 분석해드립니다',
      subtitle: 'Alt+Enter로 AI 문법 분석 실행'
    },
    {
      title: '실행 취소도 가능합니다',
      subtitle: 'Ctrl+Z로 이전 상태로 되돌리기'
    }
  ]
  
  // Select random tip index on component mount
  let currentTipIndex = $state(Math.floor(Math.random() * tips.length))
  
  // Rotate tip when text changes from empty to having text
  let previousHasText = $state(false)
  $effect(() => {
    if (hasText && !previousHasText) {
      currentTipIndex = (currentTipIndex + 1) % tips.length
    }
    previousHasText = hasText
  })
  

  // Calculate active errors (not ignored)
  let activeErrors = $derived(
    errors.filter(error => !ignoredErrors.has(error.id))
  )
  
  let hasActiveErrors = $derived(activeErrors.length > 0)
  
  // Export getters
  export function getActiveErrors() {
    return activeErrors
  }
  
  export function getHasActiveErrors() {
    return hasActiveErrors
  }

  // Get display state
  export function getDisplayState(): 'no-text' | 'checking' | 'all-applied' | 'all-ignored' | 'no-errors' | 'has-errors' | 'ready' {
    const state = (() => {
      if (!hasText) {
        return 'no-text'
      }
      if (isChecking) {
        return 'checking'
      }
      // Check all-applied state with highest priority
      // This should show even when errors are cleared
      if (allApplied === true && processedCount > 0) {
        return 'all-applied'
      }
      // If no errors remain after checking and some were processed, they were fixed
      if (hasChecked && errors.length === 0 && processedCount > 0) {
        return 'all-applied'
      }
      // All errors are ignored (still present but ignored)
      if (hasChecked && errors.length > 0 && !hasActiveErrors) {
        return 'all-ignored'
      }
      // No errors found initially
      if (hasChecked && errors.length === 0 && processedCount === 0) {
        return 'no-errors'
      }
      if (hasActiveErrors) {
        return 'has-errors'
      }
      return 'ready'
    })()
    
    return state
  }

  // Get empty state message
  export function getEmptyStateMessage() {
    const state = getDisplayState()
    switch (state) {
      case 'no-text':
        return {
          icon: 'empty',
          title: '검사할 텍스트를 입력해주세요',
          subtitle: ''
        }
      case 'checking':
        return {
          icon: 'loading',
          title: '분석 중',
          subtitle: ''
        }
      case 'all-applied':
        return {
          icon: 'success',
          title: '모든 오류를 처리했습니다',
          subtitle: `${processedCount}개 항목 처리 완료`
        }
      case 'all-ignored':
        return {
          icon: 'success',
          title: '모든 오류를 무시했습니다',
          subtitle: `${errors.length}개 항목 무시됨`
        }
      case 'no-errors':
        return {
          icon: 'success',
          title: '문제없습니다',
          subtitle: '검사 완료'
        }
      case 'ready':
        // Show tip based on current index
        const currentTip = tips[currentTipIndex]
        return {
          icon: 'info',
          title: currentTip.title,
          subtitle: currentTip.subtitle
        }
      default:
        return null
    }
  }
</script>